import { Flex } from "@chakra-ui/layout";
import { Box, Checkbox } from "@chakra-ui/react";
import {
  FormaDistribuicaoArrecadacaoEnum,
  PeriodoArrecadacaoEnum,
  TipoArrecadacaoEnum,
} from "features/arrecadacoes/enums";
import { ArrecadacaoForm } from "features/arrecadacoes/typings/form";
import useArrecadacao from "features/arrecadacoes/useArrecadacao";
import useSaveArrecadacao from "features/arrecadacoes/useSaveArrecadacao";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import {
  Control,
  useForm,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";
import OutlineButton from "components/Button/OutlineButton";
import DefaultButton from "components/Button/PrimaryButton";
import Stepper from "components/Stepper";
import getSteps from "../utils/getSteps";
import validateStep from "../utils/validateStep";
import { CloseModalButton } from "components/Button/CloseModalButton";
import useParametrosDiaVencimento from "features/condominio/hooks/useParametrosDiaVencimento";
import { useCurrentTenant } from "features/auth/tenant.store";
import { useLoading } from "components/GlobalLoading";
import { getValoresManuaisCadaUnidade } from "./../utils/getValoresManuaisCadaUnidade";

export interface FormArrecadacaoChildrenFormProps {
  control: Control<ArrecadacaoForm, object>;
  watch: UseFormWatch<ArrecadacaoForm>;
  setValue: UseFormSetValue<ArrecadacaoForm>;
  current: boolean;
  idArrecadacao?: string;
}

interface FormArrecadacaoProps {
  idArrecadacao?: string;
  onClose?: () => void;
  year: string;
  month: string;
}

const defaultValues: Partial<ArrecadacaoForm> = {
  percentualRateio: 100,
  formaExibirFracaoId: "",
  periodoArrecadacaoId: PeriodoArrecadacaoEnum.Sempre,
  formaDistribuicaoArrecadacaoId:
    FormaDistribuicaoArrecadacaoEnum.TodasUnidades,
  tipoArrecadacaoId: TipoArrecadacaoEnum.ValorIgualTodasUnidades,
  removerDestinos: [],
  valorManualUnidades: {},
  complementoAutomaticoParcelas: null,
};

function FormArrecadacao({
  idArrecadacao,
  onClose,
  year,
  month,
}: FormArrecadacaoProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [registerAnother, setRegisterAnother] = useState(false);

  const { tenant } = useCurrentTenant();

  const { data: parametrosCobranca, isLoading: isLoadingParametro } =
    useParametrosDiaVencimento(tenant.id);

  const { data, isLoading } = useArrecadacao(idArrecadacao);
  const { isLoading: isLoadingSave, mutate: handleSave } =
    useSaveArrecadacao(idArrecadacao);

  useLoading(isLoadingParametro);

  const { control, watch, handleSubmit, formState, reset, setValue } =
    useForm<ArrecadacaoForm>({
      defaultValues,
    });

  useEffect(() => {
    if (data) {
      reset({ ...data, gerarAutomatico: !!data.diaGerarAutomatico });
    }
  }, [data, reset]);

  useEffect(() => {
    if (parametrosCobranca && !idArrecadacao) {
      setValue("diaVencimento", parametrosCobranca.diaVencimento);
    }
  }, [parametrosCobranca, setValue, idArrecadacao]);

  const form = watch();

  const _steps = getSteps(form, { month, year, idArrecadacao });

  // REGRA DE PERIODO DE ARRECADAÇÃO
  const periodoArrecadacaoId = watch("periodoArrecadacaoId");

  useEffect(() => {
    if (!idArrecadacao) {
      if (periodoArrecadacaoId === PeriodoArrecadacaoEnum.Sempre) {
        //setValue("dataInicio", moment().format("YYYY-MM-DD"));
        setValue("dataFim", null);
      } else if (periodoArrecadacaoId === PeriodoArrecadacaoEnum.Unico) {
        //setValue("dataInicio", moment().startOf("month").format("YYYY-MM-DD"));
        setValue("dataFim", moment().endOf("month").format("YYYY-MM-DD"));
      }
    }
  }, [periodoArrecadacaoId, setValue, idArrecadacao]);

  useEffect(() => {
    if (
      form.dataInicio &&
      form.dataFim &&
      moment(form.dataFim).isBefore(moment(form.dataInicio), "month")
    ) {
      setValue(
        "dataFim",
        moment(form.dataInicio).add(1, "month").format("YYYY-MM-DD")
      );
    }
  }, [form.dataFim, form.dataInicio, setValue]);

  const childrenFormProps = useMemo(
    () => ({ control, watch, formState }),
    [control, formState, watch]
  );

  const maxStep = _steps.length - 1;

  const onSubmit = async (values: ArrecadacaoForm) => {
    const yearMonth = `${year}-${month}`;

    const _valoresManuais = getValoresManuaisCadaUnidade(values);

    const _data: ArrecadacaoForm = {
      ...values,
      valorManualUnidades: _valoresManuais,
      tipoArrecadacaoId:
        values.formaDistribuicaoArrecadacaoId ===
        FormaDistribuicaoArrecadacaoEnum.Consumo
          ? TipoArrecadacaoEnum.ConsumoAguaGasEnergia
          : values.tipoArrecadacaoId,
    };

    if (
      data?.periodoArrecadacaoId !== values.periodoArrecadacaoId ||
      !idArrecadacao
    ) {
      // TODO, rever isso
      _data.dataInicio =
        values.periodoArrecadacaoId !== PeriodoArrecadacaoEnum.VariosMeses
          ? moment(yearMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
          : values.dataInicio;
      _data.dataFim =
        values.periodoArrecadacaoId === PeriodoArrecadacaoEnum.Sempre
          ? null
          : values.periodoArrecadacaoId === PeriodoArrecadacaoEnum.Unico
          ? moment(yearMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
          : values.dataFim;
    }

    _data.diaGerarAutomatico = values.gerarAutomatico
      ? values.diaGerarAutomatico || null
      : null;

    handleSave(_data, {
      onSuccess: () => {
        showSuccess().then(() => {
          if (registerAnother) {
            resetForm();
          } else {
            onClose!();
          }
        });
      },
      onError: (err) => {
        showErrorAxios(err);
      },
    });
  };

  const resetForm = () => {
    setCurrentStep(0);
    reset({ ...defaultValues });
  };

  const onNext = async () => {
    if (currentStep === maxStep) {
      handleSubmit(onSubmit)();
    } else {
      setCurrentStep((step) => Math.min(step + 1, maxStep));
    }
  };

  const onPrev = () =>
    setCurrentStep((step) => {
      if (step <= 2) {
        setValue("tipoArrecadacaoId", undefined);
      }
      return Math.max(step - 1, 0);
    });

  return (
    <Flex flexDir="column" w="100%" h="100%">
      <Stepper
        steps={_steps}
        current={currentStep}
        onClickStep={setCurrentStep}
      />

      <Flex mt={8} w="100%" flex={1}>
        {_steps.map(({ component: Form, extraProps }, index) =>
          Form ? (
            <Form
              setValue={setValue}
              key={Form.name}
              {...childrenFormProps}
              {...extraProps}
              current={index === currentStep}
            />
          ) : null
        )}
      </Flex>

      <Box
        mt="32px"
        justifyContent="flex-end"
        d="flex"
        gridGap="8px"
        flexDir="row"
        alignItems="center"
        pb={4}
      >
        {currentStep === maxStep ? (
          <Checkbox
            mr="auto"
            isChecked={registerAnother}
            onChange={() => setRegisterAnother((state) => !state)}
          >
            Cadastrar outra após salvar
          </Checkbox>
        ) : null}

        <Flex flex={1} justifyContent="end" gap={2}>
          <CloseModalButton close={onClose} closeModalLabel="Cancelar" />

          {currentStep > 0 ? (
            <OutlineButton
              isDisabled={isLoading || isLoadingSave}
              onClick={onPrev}
            >
              Anterior
            </OutlineButton>
          ) : null}

          <DefaultButton
            type="button"
            isLoading={isLoading || isLoadingSave}
            isDisabled={isLoading || isLoadingSave}
            onClick={onNext}
            minWidth={"120px"}
            disabled={!validateStep(form, currentStep, maxStep) || isLoading}
          >
            {currentStep < maxStep ? "Próximo" : "Salvar"}
          </DefaultButton>
        </Flex>
      </Box>
    </Flex>
  );
}

export default FormArrecadacao;
